import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as action from '../Store/Pom.action';
import { Page_Size as fixPageSize, checkArray } from '../../../../Components/helper';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';

export const StateContext = createContext(null);

export default function PomGroupContextProvider({ children }) {
  const dispatch = useDispatch();

  const reducerState = useSelector((redState) => redState);
  const {
    PomDetails: { pomGroupsData, totalPomGroupCount },
    UserDetails: { userInfo },
    Users: { logInRights },
  } = reducerState;
  const loggedInUserId = userInfo.userId;

  const { CheckActionPermission } = useComponent();
  const pomAddEditAccess = CheckActionPermission(logInRights, 'libraries', 'pomGrading', 'addEdit');

  const pomDeleteAccess = CheckActionPermission(logInRights, 'libraries', 'pomGrading', 'delete');

  const initialSetting = {
    cardList: [],
    searchText: '',
    searchLoaded: false,
    loading: false,
    pageLoading: false,
    pageSize: fixPageSize,
    pageIndex: 1,
    hasMore: false,
    selectedIds: [],
    deleteId: null,
    showDeletePopup: false,
    showMulteDeletePopup: false,
    expandCard: '',
  };

  const [pomGroupListState, pomGroupListSetState] = useState(initialSetting);

  const isallChecked = checkArray(pomGroupListState?.cardList) && pomGroupListState?.selectedIds?.length === pomGroupListState?.cardList?.length;

  const handleCheckAll = useCallback(
    (e) => {
      if (e.target.checked) {
        const allIds = pomGroupListState?.cardList && checkArray(pomGroupListState?.cardList) && pomGroupListState?.cardList?.map((ele) => ele._id);
        if (allIds) {
          pomGroupListSetState((prevState) => ({
            ...prevState,
            selectedIds: allIds.filter((element) => element !== undefined),
          }));
        }
      } else {
        pomGroupListSetState((prevState) => ({
          ...prevState,
          selectedIds: [],
        }));
      }
    },
    [pomGroupListState?.cardList, pomGroupListSetState],
  );

  useEffect(() => {
    if (Array.isArray(pomGroupsData)) {
      pomGroupListSetState((prev) => ({
        ...prev,
        cardList: [...pomGroupListState.cardList, ...pomGroupsData],
        hasMore: totalPomGroupCount > pomGroupsData.length,
      }));
    }
  }, [pomGroupsData, totalPomGroupCount]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      let res;
      if (pomGroupListState.searchLoaded) {
        pomGroupListSetState((debouncestate) => ({
          ...debouncestate,
          pageIndex: 1,
          hasMore: true,
          cardList: [],
          loading: true,
        }));

        const reqObj = {
          pageIndex: pomGroupListState?.pageIndex,
          pageSize: pomGroupListState?.pageSize,
          searchText: pomGroupListState?.searchText,
          loggedInUserId,
          section: 'libraries',
          module: 'pomGrading',
          action: 'read',
        };
        res = await dispatch(action.fetchPomGroupsData(reqObj));
      }
      if (res) {
        pomGroupListSetState((debstate) => ({
          ...debstate,
          searchLoaded: false,
          loading: false,
        }));
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pomGroupListState.searchText, dispatch]);

  const contextValue = useMemo(() => {
    return {
      pomGroupListState,
      pomGroupListSetState,
      isallChecked,
      handleCheckAll,
      loggedInUserId,
      pomAddEditAccess,
      pomDeleteAccess,
    };
  }, [pomGroupListState, pomGroupListSetState, isallChecked, handleCheckAll, loggedInUserId, pomAddEditAccess, pomDeleteAccess]);

  useEffect(() => {
    const callFn = async () => {
      const reqObj = {
        pageIndex: 1,
        pageSize: pomGroupListState.pageSize,
        searchText: pomGroupListState?.searchText,
        loggedInUserId,
        section: 'libraries',
        module: 'pomGrading',
        action: 'read',
      };

      pomGroupListSetState((prev) => ({
        ...prev,
        loading: true,
      }));

      const res = await dispatch(action.fetchPomGroupsData(reqObj));
      if (res) {
        pomGroupListSetState((prev) => ({
          ...prev,
          loading: false,
        }));
      }
    };

    callFn();
  }, []);

  return <StateContext.Provider value={contextValue}>{children}</StateContext.Provider>;
}

export function usePomGroupContextState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the PIMModule State Provider');
  }
  return context;
}
